import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    IconButton,
    Button,
    TextField,
    InputAdornment,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
    CircularProgress,
    Grid,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Select,
    FormControl,
    InputLabel,
    RadioGroup,
    Radio,
    Checkbox,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Tabs,
    Tab,
    alpha,
    ClickAwayListener,
    ListItemIcon,
    Switch,
} from '@mui/material'
import {
    Search as SearchIcon,
    Add as AddIcon,
    Visibility as VisibilityIcon,
    Comment as CommentIcon,
    Delete as DeleteIcon,
    FilterList as FilterIcon,
    Send as SendIcon,
    Inbox as InboxIcon,
    MarkEmailRead as ReadIcon,
    Reply as ReplyIcon,
    PendingActions as PendingIcon,
    CheckCircle as CheckCircleIcon,
    Person as PersonIcon,
    Download as DownloadIcon,
    Settings as SettingsIcon,
    Label as LabelIcon,
    EditNote as DirectIcon,
    MoreVert as MoreVertIcon,
    Refresh as RefreshIcon,
    Archive as ArchiveIcon,
    Inventory2,
    Forum,
    Close as CloseIcon,
    DragIndicator,
} from '@mui/icons-material'
import { useTranslation } from '@i18n'
import { useAuthStore } from '@store/authStore'
import axios from '@api/axios'
import { directSubmissionsApi } from '@api/directSubmissions'
import { formsApi } from '@api/forms'
import Loading from '@components/Common/Loading'
import StatusChip from '@components/Common/StatusChip'
import { toast } from 'react-toastify'
import { formatRelativeTime, formatDate } from '@utils/helpers'
import { exportSubmissionsToExcel } from '@utils/exportUtils'

const STATUS_COLORS = {
    new: 'primary',
    read: 'info',
    replied: 'success',
    pending_department: 'warning',
    department_replied: 'secondary'
}

const STATUS_ICONS = {
    new: <InboxIcon fontSize="small" />,
    read: <ReadIcon fontSize="small" />,
    replied: <ReplyIcon fontSize="small" />,
    pending_department: <PendingIcon fontSize="small" />,
    department_replied: <CheckCircleIcon fontSize="small" />
}

// System columns for export
const SYSTEM_COLUMNS = [
    { id: 'submission_id', labelEn: 'Submission ID', labelAr: 'رقم التقديم' },
    { id: 'form_title', labelEn: 'Form', labelAr: 'النموذج' },
    { id: 'status', labelEn: 'Status', labelAr: 'الحالة' },
    { id: 'created_at', labelEn: 'Submitted At', labelAr: 'تاريخ التقديم' },
    { id: 'responded_by', labelEn: 'Responded By', labelAr: 'الرد من' },
    { id: 'full_name', labelEn: 'Full Name', labelAr: 'الاسم الكامل' },
]

const DirectSubmissions = () => {
    const { t, language } = useTranslation()
    const { user } = useAuthStore()
    const navigate = useNavigate()
    const isRTL = language === 'ar'

    // State
    const [submissions, setSubmissions] = useState([])
    const [stats, setStats] = useState(null)
    const [loading, setLoading] = useState(true)
    const [statsLoading, setStatsLoading] = useState(true)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(20)
    const [total, setTotal] = useState(0)
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState('')
    const [form, setForm] = useState(null)
    const [categoryMap, setCategoryMap] = useState({})
    const [allCategories, setAllCategories] = useState([])

    // Action menu (status change)
    const [actionAnchor, setActionAnchor] = useState(null)
    const [selectedRow, setSelectedRow] = useState(null)

    // Live search state
    const [searchResults, setSearchResults] = useState([])
    const [searchLoading, setSearchLoading] = useState(false)
    const [showSearchResults, setShowSearchResults] = useState(false)
    const searchRef = useRef(null)
    const searchTimerRef = useRef(null)

    // Comments state
    const [commentsDialogOpen, setCommentsDialogOpen] = useState(false)
    const [selectedSubmission, setSelectedSubmission] = useState(null)
    const [comments, setComments] = useState([])
    const [newComment, setNewComment] = useState('')
    const [commentsLoading, setCommentsLoading] = useState(false)

    // Create submission dialog
    const [createDialogOpen, setCreateDialogOpen] = useState(false)
    const [formFields, setFormFields] = useState(null)
    const [submissionData, setSubmissionData] = useState({})

    // Export state
    const [exportDialogOpen, setExportDialogOpen] = useState(false)
    const [exportLanguage, setExportLanguage] = useState(language)
    const [exporting, setExporting] = useState(false)
    const [selectedExportFields, setSelectedExportFields] = useState(null)
    const [selectedSystemColumns, setSelectedSystemColumns] = useState(null)
    const [creating, setCreating] = useState(false)

    // Archive tab
    const [showArchived, setShowArchived] = useState(false)

    // Row selection (for bulk operations, matching Submissions page)
    const [selectedSubmissions, setSelectedSubmissions] = useState([])

    // Column management — default columns identical to Submissions page
    const DS_DEFAULT_COLUMNS = [
        { id: 'submission_id', labelEn: 'Submission ID', labelAr: 'رقم الطلب',   visible: true,  required: true  },
        { id: 'created_by',    labelEn: 'Created By',    labelAr: 'مقدم الطلب',  visible: true,  required: false },
        { id: 'preview',       labelEn: 'Preview',       labelAr: 'معاينة',       visible: true,  required: false },
        { id: 'status',        labelEn: 'Status',        labelAr: 'الحالة',       visible: true,  required: false },
        { id: 'responded_by',  labelEn: 'Responded By',  labelAr: 'الرد من',      visible: false, required: false },
        { id: 'categories',    labelEn: 'Categories',    labelAr: 'التصنيفات',     visible: false, required: false },
        { id: 'form',          labelEn: 'Form',          labelAr: 'النموذج',      visible: false, required: false },
        { id: 'date',          labelEn: 'Date',          labelAr: 'التاريخ',      visible: true,  required: false },
    ]
    const DS_STORAGE_KEY = 'directSubmissionColumnSettings'

    const [columns, setColumns] = useState(DS_DEFAULT_COLUMNS)
    const [columnSettingsOpen, setColumnSettingsOpen] = useState(false)
    const [draggedItem, setDraggedItem] = useState(null)
    const [allFormFields, setAllFormFields] = useState([])

    // Fetch all forms to extract fields for column settings
    useEffect(() => {
        const fetchAllFields = async () => {
            try {
                const response = await formsApi.getAll()
                const forms = response.data || []
                const fieldsMap = new Map()

                forms.forEach((form) => {
                    let fields = form.fields
                    if (typeof fields === 'string') {
                        try { fields = JSON.parse(fields) } catch (e) { fields = [] }
                    }
                    if (Array.isArray(fields)) {
                        fields.forEach(field => {
                            if (!fieldsMap.has(field.id)) {
                                fieldsMap.set(field.id, {
                                    id: field.id,
                                    label_en: field.label_en || field.label,
                                    label_ar: field.label_ar || field.label,
                                    type: field.type,
                                    options: field.options,
                                    formId: form.id,
                                    formName: form.title_en || form.title
                                })
                            }
                        })
                    }
                })
                setAllFormFields(Array.from(fieldsMap.values()))
            } catch (error) {
                console.error('Error fetching forms for columns:', error)
            }
        }
        fetchAllFields()
    }, [])

    // Manage columns mapping when allFormFields or language changes
    useEffect(() => {
        const dynamicColumns = allFormFields.map(field => ({
            id: `field_${field.id}`,
            labelEn: field.label_en,
            labelAr: field.label_ar,
            type: 'form_field',
            fieldId: field.id,
            fieldType: field.type,
            fieldOptions: field.options,
            visible: false, 
            required: false
        }))

        const allCols = [...DS_DEFAULT_COLUMNS, ...dynamicColumns]
        const saved = localStorage.getItem(DS_STORAGE_KEY)

        if (saved) {
            try {
                const parsed = JSON.parse(saved)
                const merged = allCols.map(defaultCol => {
                    const savedCol = parsed.find(c => c.id === defaultCol.id)
                    return savedCol ? { ...defaultCol, visible: savedCol.visible } : defaultCol
                })
                const ordered = []
                parsed.forEach(savedCol => {
                    const col = merged.find(c => c.id === savedCol.id)
                    if (col) ordered.push(col)
                })
                merged.forEach(col => {
                    if (!ordered.find(c => c.id === col.id)) ordered.push(col)
                })
                setColumns(ordered)
            } catch (e) {
                setColumns(allCols)
            }
        } else {
            setColumns(allCols)
        }
    }, [allFormFields, isRTL])


    // Fetch form info
    useEffect(() => {
        if (user?.form_id) {
            formsApi.getById(user.form_id).then(res => {
                if (res.data) {
                    setForm(res.data)
                    setFormFields(res.data.fields || [])
                }
            }).catch(console.error)
        }
    }, [user?.form_id])

    // Fetch submissions
    const fetchSubmissions = async () => {
        setLoading(true)
        try {
            const res = await directSubmissionsApi.getAll({
                page: page + 1,
                perPage: rowsPerPage,
                search: search || undefined,
                status: statusFilter || undefined,
                archived: showArchived,
            })
            if (res.data) {
                setSubmissions(res.data.data || [])
                setTotal(res.data.total || 0)
            }
        } catch (error) {
            console.error('Error fetching direct submissions:', error)
            toast.error(t('common.error'))
        } finally {
            setLoading(false)
        }
    }

    // Fetch stats
    const fetchStats = async () => {
        setStatsLoading(true)
        try {
            const res = await directSubmissionsApi.getStats()
            if (res.data) {
                setStats(res.data)
            }
        } catch (error) {
            console.error('Error fetching stats:', error)
        } finally {
            setStatsLoading(false)
        }
    }

    useEffect(() => {
        fetchSubmissions()
        fetchStats()
    }, [page, rowsPerPage, search, statusFilter, showArchived])

    // Fetch categories for visible submissions in bulk
    useEffect(() => {
        const fetchCategories = async () => {
            if (!submissions || submissions.length === 0) return
            try {
                const submissionIds = submissions.map(s => s.id).join(',')
                const response = await axios.get(`/submissions/categories/bulk`, {
                    params: { submission_ids: submissionIds }
                })
                // Support axios interceptor that might return response.data directly or full response
                const data = response?.data || response;
                if (data && typeof data === 'object' && !Array.isArray(data)) {
                    setCategoryMap(data)
                    // Build unique categories list for filtering if needed later
                    const catSet = new Map()
                    Object.values(data).forEach(cats => {
                        if (Array.isArray(cats)) {
                            cats.forEach(cat => catSet.set(cat.id, cat))
                        }
                    })
                    setAllCategories(Array.from(catSet.values()))
                }
            } catch (err) {
                console.error('Failed to fetch categories:', err)
            }
        }
        fetchCategories()
    }, [submissions])

    // Debounced live search — fires on every keystroke with 300ms delay
    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
        if (!search || search.length < 2) {
            setSearchResults([])
            setShowSearchResults(false)
            return
        }
        setSearchLoading(true)
        searchTimerRef.current = setTimeout(async () => {
            try {
                const response = await directSubmissionsApi.search(search)
                const results = response?.data || response || []
                setSearchResults(Array.isArray(results) ? results : [])
                setShowSearchResults(true)
            } catch (err) {
                console.error('Live search error:', err)
                setSearchResults([])
            } finally {
                setSearchLoading(false)
            }
        }, 300)
        return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current) }
    }, [search])

    // Handle status update
    const handleStatusUpdate = async (id, newStatus) => {
        try {
            await directSubmissionsApi.updateStatus(id, newStatus)
            toast.success(t('submissions.statusUpdated'))
            fetchSubmissions()
            fetchStats()
        } catch (error) {
            console.error('Error updating status:', error)
            toast.error(t('common.error'))
        }
    }

    // Handle comments
    const openComments = async (submission) => {
        setSelectedSubmission(submission)
        setCommentsDialogOpen(true)
        setCommentsLoading(true)
        try {
            const res = await directSubmissionsApi.getComments(submission.id)
            setComments(res.data?.data || [])
        } catch (error) {
            console.error('Error fetching comments:', error)
        } finally {
            setCommentsLoading(false)
        }
    }

    const handleAddComment = async () => {
        if (!newComment.trim() || !selectedSubmission) return
        try {
            await directSubmissionsApi.addComment(selectedSubmission.id, newComment)
            setNewComment('')
            // Refresh comments
            const res = await directSubmissionsApi.getComments(selectedSubmission.id)
            setComments(res.data?.data || [])
            toast.success(t('common.success'))
        } catch (error) {
            console.error('Error adding comment:', error)
            toast.error(t('common.error'))
        }
    }

    const handleDeleteComment = async (commentId) => {
        try {
            await directSubmissionsApi.deleteComment(commentId)
            setComments(comments.filter(c => c.id !== commentId))
            toast.success(t('common.deleted'))
        } catch (error) {
            console.error('Error deleting comment:', error)
            toast.error(t('common.error'))
        }
    }

    // ── Column management ──────────────────────────────────────────────────
    const saveColumnSettings = (cols) => {
        try { localStorage.setItem(DS_STORAGE_KEY, JSON.stringify(cols.map(c => ({ id: c.id, visible: c.visible })))) } catch {}
    }

    const handleColumnToggle = (id) => {
        setColumns(prev => {
            const next = prev.map(c => c.id === id && !c.required ? { ...c, visible: !c.visible } : c)
            saveColumnSettings(next)
            return next
        })
    }

    const handleDragStart = (e, index) => { setDraggedItem(index); e.dataTransfer.effectAllowed = 'move' }
    const handleDragOver = (e, index) => {
        e.preventDefault()
        if (draggedItem === null || draggedItem === index) return
        setColumns(prev => {
            const next = [...prev]
            const [removed] = next.splice(draggedItem, 1)
            next.splice(index, 0, removed)
            setDraggedItem(index)
            return next
        })
    }
    const handleDragEnd = () => { setDraggedItem(null); saveColumnSettings(columns) }

    // ── Row selection ──────────────────────────────────────────────────────
    const selectSubmission = (id) =>
        setSelectedSubmissions(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
    const selectAllSubmissions = (ids) => setSelectedSubmissions(ids)
    const clearSelection = () => setSelectedSubmissions([])

    // ── Archive / Restore ─────────────────────────────────────────────────
    const handleArchive = async (id) => {
        try {
            await directSubmissionsApi.archive(id)
            toast.success(isRTL ? 'تم الأرشفة' : 'Archived')
            fetchSubmissions()
            fetchStats()
        } catch { toast.error(t('common.error')) }
    }

    const handleRestore = async (id) => {
        try {
            await directSubmissionsApi.restore(id)
            toast.success(isRTL ? 'تم الاستعادة' : 'Restored')
            fetchSubmissions()
            fetchStats()
        } catch { toast.error(t('common.error')) }
    }

    // ── renderCellContent ─────────────────────────────────────────────────
    // ── getRespondedBy helper ──────────────────────────────────────────────
    const getRespondedBy = (submission) => {
        if (!submission) return '-';
        if (submission.status === 'department_replied') {
            return isRTL ? submission.department_replied_by_name_ar : submission.department_replied_by_name_en
        }
        if (submission.status === 'replied') {
            return isRTL ? submission.responded_by_name_ar : submission.responded_by_name_en
        }
        if (submission.status === 'read' || submission.status === 'pending_department') {
            return submission.read_by_name_en || submission.read_by_name_ar || '-'
        }
        return '-'
    }

    // ── renderCellContent ─────────────────────────────────────────────────
    const renderCell = (submission, colId) => {
        // Handle Dynamic Form Fields Mapping
        if (colId.startsWith('field_')) {
            const fieldId = colId.replace('field_', '');
            let value = submission.data?.[fieldId];
            const column = columns.find(c => c.id === colId);
            
            if (!value || value === '') {
                return <Typography variant="body2" color="text.secondary">-</Typography>;
            }

            if (!column) return <Typography variant="body2">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</Typography>;

            switch (column.fieldType) {
                case 'select':
                case 'radio': {
                    const option = column.fieldOptions?.find(opt => (opt?.value || opt) === value);
                    let displayValue = value;
                    if (option) {
                        displayValue = (typeof option === 'object') 
                            ? (language === 'ar' ? (option.label_ar || option.label || option.value || '') : (option.label_en || option.label || option.value || '')) 
                            : option;
                    }
                    return <Typography variant="body2">{typeof displayValue === 'object' ? JSON.stringify(displayValue) : String(displayValue)}</Typography>;
                }
                case 'checkbox':
                    if (Array.isArray(value)) {
                        return (
                            <Typography variant="body2">
                                {value.map(v => {
                                    const opt = column.fieldOptions?.find(o => (o?.value || o) === v);
                                    if (opt) {
                                        return (typeof opt === 'object') 
                                            ? (language === 'ar' ? (opt.label_ar || opt.label || opt.value || '') : (opt.label_en || opt.label || opt.value || '')) 
                                            : opt;
                                    }
                                    return (typeof v === 'object' ? JSON.stringify(v) : String(v));
                                }).join(', ')}
                            </Typography>
                        );
                    }
                    return <Typography variant="body2">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</Typography>;
                case 'file':
                    return (
                        <Typography variant="body2" color="primary.main" sx={{ textDecoration: 'underline' }}>
                            {typeof value === 'string' ? value.split('/').pop() : 'File'}
                        </Typography>
                    );
                case 'date':
                    return (
                        <Typography variant="body2">
                            {formatDate(value, 'PP', language)}
                        </Typography>
                    );
                default:
                    return (
                        <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </Typography>
                    );
            }
        }

        const dataVals = submission.data
            ? Object.values(submission.data).filter(v => typeof v === 'string' && v.trim().length > 0)
            : [];

        switch (colId) {
            case 'submission_id':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography variant="body2" fontWeight={700} fontFamily="monospace">
                            {submission.submission_id}
                        </Typography>
                        <Chip
                            icon={<DirectIcon sx={{ fontSize: '13px !important' }} />}
                            label={isRTL ? 'مباشر' : 'Direct'}
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: 13 } }}
                        />
                    </Box>
                )
            case 'created_by':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: '0.75rem' }}>
                            {(isRTL
                                ? (submission.created_by_name_ar || submission.created_by_name_en)
                                : (submission.created_by_name_en || submission.created_by_name_ar)
                            )?.[0]?.toUpperCase() || '?'}
                        </Avatar>
                        <Typography variant="body2">
                            {isRTL
                                ? submission.created_by_name_ar || submission.created_by_name_en
                                : submission.created_by_name_en || submission.created_by_name_ar}
                        </Typography>
                    </Box>
                )
            case 'preview':
                return (
                    <Typography variant="body2" color="text.secondary"
                        sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {dataVals.slice(0, 2).join(' · ').slice(0, 100) || '—'}
                    </Typography>
                )
            case 'status':
                return <StatusChip status={submission.status} />
            case 'responded_by':
                const respondedBy = getRespondedBy(submission);
                return respondedBy !== '-' ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="body2">{respondedBy}</Typography>
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                )
            case 'categories':
                const cats = categoryMap[submission.id] || []
                if (cats.length === 0) return <Typography variant="body2" color="text.secondary">-</Typography>
                return (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {cats.map(cat => (
                            <Chip
                                key={cat.id}
                                icon={<LabelIcon sx={{ fontSize: 14 }} />}
                                label={language === 'ar' ? (cat.name_ar || cat.name_en) : cat.name_en}
                                size="small"
                                sx={{
                                    height: 22,
                                    fontSize: '0.7rem',
                                    backgroundColor: cat.color ? cat.color + '22' : 'transparent',
                                    color: cat.color || 'inherit',
                                    borderColor: cat.color || 'inherit',
                                    '& .MuiChip-icon': { color: cat.color || 'inherit', fontSize: 14 }
                                }}
                                variant="outlined"
                            />
                        ))}
                    </Box>
                )
            case 'form':
                return (
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 160 }}>
                        {isRTL ? submission.form_title_ar : submission.form_title}
                    </Typography>
                )
            case 'date':
                return (
                    <Tooltip title={formatDate(submission.created_at, 'PPP p', language)}>
                        <Typography variant="body2" color="text.secondary">
                            {formatRelativeTime(submission.created_at, language)}
                        </Typography>
                    </Tooltip>
                )
            default:
                return null
        }
    }

    const handleCreateSubmission = async () => {
        setCreating(true)
        try {
            await directSubmissionsApi.create(user.form_id, submissionData, language)
            toast.success(t('submissions.created'))
            setCreateDialogOpen(false)
            setSubmissionData({})
            fetchSubmissions()
            fetchStats()
        } catch (error) {
            console.error('Error creating submission:', error)
            toast.error(t('common.error'))
        } finally {
            setCreating(false)
        }
    }

    // Export form fields state (loaded dynamically for the export dialog)
    const [exportFormFields, setExportFormFields] = useState([])

    // Handle export
    const handleExportToExcel = async () => {
        if (submissions.length === 0) {
            toast.warning(language === 'ar' ? 'لا توجد بيانات للتصدير' : 'No data to export')
            return
        }
        setExportLanguage(language)
        setSelectedExportFields(null)
        setSelectedSystemColumns(null)

        // Try to get fields from different sources
        let fields = []

        // Source 1: from form state (loaded via user.form_id)
        if (form?.fields) {
            fields = typeof form.fields === 'string' ? JSON.parse(form.fields) : form.fields
        }

        // Source 2: from formFields state
        if (fields.length === 0 && formFields && Array.isArray(formFields) && formFields.length > 0) {
            fields = formFields
        }

        // Source 3: extract form_id from submissions and fetch
        if (fields.length === 0 && submissions.length > 0) {
            try {
                const formIds = [...new Set(submissions.map(s => s.form_id).filter(Boolean))]
                if (formIds.length > 0) {
                    const formRes = await formsApi.getById(formIds[0])
                    if (formRes.data?.fields) {
                        fields = typeof formRes.data.fields === 'string'
                            ? JSON.parse(formRes.data.fields)
                            : formRes.data.fields
                    }
                }
            } catch (err) {
                console.error('Error fetching form fields for export:', err)
            }
        }

        // Filter out non-data fields
        fields = Array.isArray(fields) ? fields.filter(f => !['captcha', 'note'].includes(f.type)) : []
        setExportFormFields(fields)
        setExportDialogOpen(true)
    }

    const handleConfirmExport = async () => {
        setExporting(true)
        try {
            // Fetch ALL submissions (not paginated) for export
            const res = await directSubmissionsApi.getAll({
                page: 1,
                perPage: 10000,
                search: search || undefined,
                status: statusFilter || undefined
            })
            const allSubmissions = res.data?.data || submissions

            const filename = `direct_submissions_${new Date().toISOString().split('T')[0]}.xlsx`
            exportSubmissionsToExcel(allSubmissions, exportFormFields, exportLanguage, selectedExportFields, filename, selectedSystemColumns)
            setExportDialogOpen(false)
            toast.success(language === 'ar' ? 'تم التصدير بنجاح' : 'Export successful')
        } catch (error) {
            console.error('Export error:', error)
            toast.error(language === 'ar' ? 'خطأ في التصدير' : 'Export failed')
        } finally {
            setExporting(false)
        }
    }

    // Check if direct submissions is enabled
    // Note: enable_direct_submissions is a top-level form column, not in settings JSON
    if (form && !form.enable_direct_submissions && user?.user_type !== 'admin') {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                    {t('directSubmissions.notEnabled', 'Direct submissions is not enabled for this form.')}
                </Typography>
            </Box>
        )
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" fontWeight="bold">
                        {t('directSubmissions.title', 'Direct Submissions')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('directSubmissions.subtitle', 'Manage submissions created directly by form users')}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Tooltip title={isRTL ? 'إعدادات الأعمدة' : 'Column Settings'}>
                        <IconButton onClick={() => setColumnSettingsOpen(true)}>
                            <SettingsIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('common.refresh')}>
                        <IconButton onClick={() => { fetchSubmissions(); fetchStats() }} disabled={loading}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportToExcel}
                        disabled={submissions.length === 0}
                    >
                        {language === 'ar' ? 'تصدير Excel' : 'Export Excel'}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateDialogOpen(true)}
                    >
                        {t('directSubmissions.create', 'New Submission')}
                    </Button>
                </Box>
            </Box>

            {/* Stats Cards */}
            {!statsLoading && stats && (() => {
                const statCards = [
                    {
                        key: 'total',
                        label: t('stats.total'),
                        value: stats.total || 0,
                        icon: <InboxIcon />,
                        color: '#1a3a6b',
                        bgColor: 'rgba(102, 126, 234, 0.1)',
                        filter: '',
                    },
                    {
                        key: 'new',
                        label: t('stats.new'),
                        value: stats.new_count || 0,
                        icon: <InboxIcon />,
                        color: '#3b82f6',
                        bgColor: 'rgba(59, 130, 246, 0.1)',
                        filter: 'new',
                    },
                    {
                        key: 'read',
                        label: t('stats.read'),
                        value: stats.read_count || 0,
                        icon: <ReadIcon />,
                        color: '#10b981',
                        bgColor: 'rgba(16, 185, 129, 0.1)',
                        filter: 'read',
                    },
                    {
                        key: 'replied',
                        label: t('stats.replied'),
                        value: stats.replied_count || 0,
                        icon: <ReplyIcon />,
                        color: '#8b5cf6',
                        bgColor: 'rgba(139, 92, 246, 0.1)',
                        filter: 'replied',
                    },
                    {
                        key: 'pending_department',
                        label: t('stats.pendingDepartment'),
                        value: stats.pending_department_count || 0,
                        icon: <PendingIcon />,
                        color: '#f59e0b',
                        bgColor: 'rgba(245, 158, 11, 0.1)',
                        filter: 'pending_department',
                    },
                ]
                return (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {statCards.map((card) => {
                            const isActive = statusFilter === card.filter
                            return (
                                <Grid item xs={12} sm={6} md key={card.key}>
                                    <Paper
                                        onClick={() => {
                                            setStatusFilter(card.filter)
                                            setPage(0)
                                        }}
                                        sx={{
                                            p: 2.5,
                                            cursor: 'pointer',
                                            borderRadius: 3,
                                            transition: 'all 0.2s ease',
                                            borderBottom: isActive ? `3px solid ${card.color}` : '3px solid transparent',
                                            boxShadow: isActive ? 3 : 1,
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: 4,
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            <Box>
                                                <Typography
                                                    variant="h3"
                                                    fontWeight={700}
                                                    sx={{ color: card.color }}
                                                >
                                                    {card.value}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mt: 0.5 }}
                                                >
                                                    {card.label}
                                                </Typography>
                                            </Box>
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    bgcolor: card.bgColor,
                                                    color: card.color,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                {card.icon}
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            )
                        })}
                    </Grid>
                )
            })()}

            {/* Tabs: Active / Archived */}
            <Paper sx={{ mb: 2, borderRadius: 2 }}>
                <Tabs
                    value={showArchived ? 1 : 0}
                    onChange={(_, v) => { setShowArchived(v === 1); setPage(0); clearSelection() }}
                    sx={{ px: 2 }}
                >
                    <Tab label={isRTL ? 'الطلبات النشطة' : 'Active Submissions'} />
                    <Tab label={isRTL ? 'المؤرشف' : 'Archived'} />
                </Tabs>
                <Divider />

                {/* Filters bar */}
                <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Live Search */}
                    <Box sx={{ flex: 1, minWidth: 250, position: 'relative' }} ref={searchRef}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder={isRTL ? 'ابحث في الطلبات...' : 'Search submissions…'}
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(0) }}
                            onFocus={() => { if (searchResults.length > 0) setShowSearchResults(true) }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start"><SearchIcon /></InputAdornment>
                                ),
                                endAdornment: searchLoading ? (
                                    <InputAdornment position="end"><CircularProgress size={18} /></InputAdornment>
                                ) : null,
                            }}
                        />

                        {/* Elevation-8 results dropdown — matches Submissions page class MuiPaper-elevation8 */}
                        {showSearchResults && searchResults.length > 0 && (
                            <ClickAwayListener onClickAway={() => setShowSearchResults(false)}>
                                <Paper
                                    elevation={8}
                                    sx={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        zIndex: 1300,
                                        maxHeight: 420,
                                        overflowY: 'auto',
                                        mt: 0.5,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    {/* Header row */}
                                    <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                            {isRTL
                                                ? `${searchResults.length} نتيجة — كل الأنواع والمؤرشف`
                                                : `${searchResults.length} results — all types + archived`}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {['chat', 'direct', 'normal'].map(type => {
                                                const count = searchResults.filter(r => r.source === type).length
                                                if (count === 0) return null
                                                const cfg = {
                                                    chat:   { label: isRTL ? 'محادثة' : 'Chat',   color: 'info',    icon: <Forum sx={{ fontSize: 11 }} /> },
                                                    direct: { label: isRTL ? 'مباشر' : 'Direct',   color: 'warning', icon: <DirectIcon sx={{ fontSize: 11 }} /> },
                                                    normal: { label: isRTL ? 'طلب' : 'Form',       color: 'default', icon: <SendIcon sx={{ fontSize: 11 }} /> },
                                                }[type]
                                                return (
                                                    <Chip key={type} icon={cfg.icon} label={`${count} ${cfg.label}`} size="small"
                                                        color={cfg.color} variant="outlined"
                                                        sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-icon': { fontSize: 11 } }} />
                                                )
                                            })}
                                        </Box>
                                    </Box>

                                    {/* Result rows */}
                                    {searchResults.map((result) => (
                                        <Box
                                            key={result.id}
                                            onClick={() => { setShowSearchResults(false); navigate(`/submissions/${result.id}`) }}
                                            sx={{
                                                px: 2, py: 1.5,
                                                cursor: 'pointer',
                                                borderBottom: '1px solid',
                                                borderColor: 'divider',
                                                '&:hover': { bgcolor: 'action.hover' },
                                                '&:last-child': { borderBottom: 'none' },
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                            }}
                                        >
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3, flexWrap: 'wrap' }}>
                                                    {result.source === 'chat' && <Forum sx={{ fontSize: 15, color: 'info.main' }} />}
                                                    {result.source === 'direct' && <DirectIcon sx={{ fontSize: 15, color: 'warning.main' }} />}
                                                    {(!result.source || result.source === 'normal') && <SendIcon sx={{ fontSize: 13, color: 'success.main' }} />}
                                                    <Typography variant="body2" fontWeight={700} noWrap>{result.submission_id}</Typography>
                                                    <StatusChip status={result.status} size="small" />
                                                    {result.archived == 1 && (
                                                        <Chip label={isRTL ? 'مؤرشف' : 'Archived'} size="small"
                                                            icon={<Inventory2 sx={{ fontSize: '14px !important' }} />}
                                                            sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'warning.light', color: 'warning.contrastText' }} />
                                                    )}
                                                </Box>
                                                {result.preview && (
                                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                                        {result.preview}
                                                    </Typography>
                                                )}
                                                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
                                                    {isRTL ? result.form_title_ar : result.form_title}
                                                    {' · '}
                                                    {formatRelativeTime(result.created_at, language)}
                                                </Typography>
                                            </Box>
                                            <VisibilityIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                                        </Box>
                                    ))}
                                </Paper>
                            </ClickAwayListener>
                        )}

                        {/* No results */}
                        {showSearchResults && searchResults.length === 0 && search.length >= 2 && !searchLoading && (
                            <ClickAwayListener onClickAway={() => setShowSearchResults(false)}>
                                <Paper elevation={8} sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1300, mt: 0.5, borderRadius: 2, p: 2, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {isRTL ? 'لم يتم العثور على نتائج' : 'No results found'}
                                    </Typography>
                                </Paper>
                            </ClickAwayListener>
                        )}
                    </Box>

                    {/* Status filter */}
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>{t('submissions.status')}</InputLabel>
                        <Select
                            value={statusFilter}
                            label={t('submissions.status')}
                            onChange={(e) => { setStatusFilter(e.target.value); setPage(0) }}
                        >
                            <MenuItem value="">{t('common.all')}</MenuItem>
                            <MenuItem value="new">{t('status.new')}</MenuItem>
                            <MenuItem value="read">{t('status.read')}</MenuItem>
                            <MenuItem value="replied">{t('status.replied')}</MenuItem>
                            <MenuItem value="pending_department">{t('status.pendingDepartment')}</MenuItem>
                            <MenuItem value="department_replied">{t('status.departmentReplied')}</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Clear filters */}
                    {(search || statusFilter) && (
                        <Button size="small" onClick={() => { setSearch(''); setStatusFilter(''); setPage(0) }}>
                            {isRTL ? 'مسح الفلاتر' : 'Clear Filters'}
                        </Button>
                    )}
                </Box>
            </Paper>

            {/* Table */}
            <Paper sx={{ borderRadius: 2 }}>
                {loading ? (
                    <Box sx={{ p: 4 }}><Loading message={t('common.loading')} /></Box>
                ) : submissions.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <DirectIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                        <Typography color="text.secondary">{t('submissions.noSubmissions')}</Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        {/* Checkbox select-all */}
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedSubmissions.length === submissions.length && submissions.length > 0}
                                                indeterminate={selectedSubmissions.length > 0 && selectedSubmissions.length < submissions.length}
                                                onChange={(e) =>
                                                    e.target.checked
                                                        ? selectAllSubmissions(submissions.map(s => s.id))
                                                        : clearSelection()
                                                }
                                            />
                                        </TableCell>
                                        {/* Dynamic column headers */}
                                        {columns.filter(c => c.visible).map(col => (
                                            <TableCell key={col.id}>
                                                {isRTL ? col.labelAr : col.labelEn}
                                            </TableCell>
                                        ))}
                                        <TableCell align={isRTL ? 'left' : 'right'}>
                                            {t('common.actions')}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {submissions.map((submission) => (
                                        <TableRow
                                            key={submission.id}
                                            hover
                                            selected={selectedSubmissions.includes(submission.id)}
                                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                                            onClick={() => navigate(`/submissions/${submission.id}`)}
                                        >
                                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                                <Checkbox
                                                    checked={selectedSubmissions.includes(submission.id)}
                                                    onChange={() => selectSubmission(submission.id)}
                                                />
                                            </TableCell>
                                            {columns.filter(c => c.visible).map(col => (
                                                <TableCell key={col.id}>
                                                    {renderCell(submission, col.id)}
                                                </TableCell>
                                            ))}
                                            <TableCell align={isRTL ? 'left' : 'right'} onClick={(e) => e.stopPropagation()}>
                                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: isRTL ? 'flex-start' : 'flex-end' }}>
                                                    <Tooltip title={isRTL ? 'عرض' : 'View'}>
                                                        <IconButton size="small" color="primary"
                                                            onClick={() => navigate(`/submissions/${submission.id}`)}>
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={isRTL ? 'تعليقات' : 'Comments'}>
                                                        <IconButton size="small"
                                                            onClick={() => openComments(submission)}>
                                                            <CommentIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={isRTL ? 'تغيير الحالة' : 'Change Status'}>
                                                        <IconButton size="small"
                                                            onClick={(e) => { setSelectedRow(submission); setActionAnchor(e.currentTarget) }}>
                                                            <MoreVertIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {!showArchived ? (
                                                        <Tooltip title={isRTL ? 'أرشفة' : 'Archive'}>
                                                            <IconButton size="small" color="warning"
                                                                onClick={() => handleArchive(submission.id)}>
                                                                <ArchiveIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title={isRTL ? 'استعادة' : 'Restore'}>
                                                            <IconButton size="small" color="success"
                                                                onClick={() => handleRestore(submission.id)}>
                                                                <RefreshIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
                            rowsPerPageOptions={[10, 20, 50, 100, { value: -1, label: isRTL ? 'الكل' : 'All' }]}
                        />
                    </>
                )}
            </Paper>


            {/* Status Change Menu */}
            <Menu
                anchorEl={actionAnchor}
                open={Boolean(actionAnchor)}
                onClose={() => { setActionAnchor(null); setSelectedRow(null) }}
            >
                <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.5, display: 'block', fontWeight: 600 }}>
                    {isRTL ? 'تغيير الحالة' : 'Change Status'}
                </Typography>
                <Divider />
                {['new', 'read', 'replied', 'pending_department', 'department_replied'].map(s => (
                    <MenuItem
                        key={s}
                        selected={selectedRow?.status === s}
                        onClick={() => {
                            if (selectedRow) handleStatusUpdate(selectedRow.id, s)
                            setActionAnchor(null)
                            setSelectedRow(null)
                        }}
                    >
                        <StatusChip status={s} />
                    </MenuItem>
                ))}
            </Menu>

            {/* Comments Dialog */}
            <Dialog open={commentsDialogOpen} onClose={() => setCommentsDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {t('directSubmissions.internalComments', 'Internal Comments')}
                    {selectedSubmission && (
                        <Typography variant="body2" color="text.secondary">
                            {selectedSubmission.submission_id}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    {commentsLoading ? (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : comments.length === 0 ? (
                        <Typography color="text.secondary" textAlign="center" py={2}>
                            {t('directSubmissions.noComments', 'No comments yet')}
                        </Typography>
                    ) : (
                        <List>
                            {comments.map((comment) => (
                                <ListItem
                                    key={comment.id}
                                    alignItems="flex-start"
                                    secondaryAction={
                                        (comment.user_id === user?.id || user?.user_type === 'admin') && (
                                            <IconButton
                                                edge="end"
                                                size="small"
                                                onClick={() => handleDeleteComment(comment.id)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        )
                                    }
                                >
                                    <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                                            <PersonIcon />
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="subtitle2">
                                                    {language === 'ar'
                                                        ? comment.user_name_ar || comment.user_name_en
                                                        : comment.user_name_en || comment.user_name_ar}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {format(new Date(comment.created_at), 'yyyy-MM-dd HH:mm')}
                                                </Typography>
                                            </Box>
                                        }
                                        secondary={comment.comment}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder={t('directSubmissions.addComment', 'Add a comment...')}
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                        />
                        <IconButton color="primary" onClick={handleAddComment} disabled={!newComment.trim()}>
                            <SendIcon />
                        </IconButton>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCommentsDialogOpen(false)}>
                        {t('common.close')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Create Submission Dialog */}
            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {t('directSubmissions.createNew', 'Create New Submission')}
                </DialogTitle>
                <DialogContent dividers>
                    {formFields && formFields.length > 0 ? (
                        <Grid container spacing={2} sx={{ pt: 1 }}>
                            {formFields
                                .filter(field => field.type !== 'captcha')
                                .map((field) => {
                                    const fieldLabel = language === 'ar' ? (field.label_ar || field.label_en) : (field.label_en || field.label_ar)
                                    const fieldValue = submissionData[field.id] || ''
                                    const fieldWidth = field.width === '50' ? 6 : field.width === '33' ? 4 : 12

                                    const handleChange = (value) => {
                                        setSubmissionData({
                                            ...submissionData,
                                            [field.id]: value
                                        })
                                    }

                                    // Get options for select/radio/checkbox fields
                                    const options = field.options || []

                                    // Render based on field type
                                    switch (field.type) {
                                        case 'select':
                                        case 'dropdown':
                                            return (
                                                <Grid item xs={12} md={fieldWidth} key={field.id}>
                                                    <FormControl fullWidth required={field.required}>
                                                        <InputLabel>{fieldLabel}</InputLabel>
                                                        <Select
                                                            value={fieldValue}
                                                            label={fieldLabel}
                                                            onChange={(e) => handleChange(e.target.value)}
                                                        >
                                                            {options.map((opt, idx) => (
                                                                <MenuItem key={idx} value={opt.value || opt.label_en || opt}>
                                                                    {language === 'ar' ? (opt.label_ar || opt.label_en || opt) : (opt.label_en || opt.label_ar || opt)}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                            )

                                        case 'radio':
                                            return (
                                                <Grid item xs={12} md={fieldWidth} key={field.id}>
                                                    <FormControl component="fieldset" required={field.required}>
                                                        <FormLabel component="legend">{fieldLabel}{field.required && ' *'}</FormLabel>
                                                        <RadioGroup
                                                            value={fieldValue}
                                                            onChange={(e) => handleChange(e.target.value)}
                                                        >
                                                            {options.map((opt, idx) => (
                                                                <FormControlLabel
                                                                    key={idx}
                                                                    value={opt.value || opt.label_en || opt}
                                                                    control={<Radio />}
                                                                    label={language === 'ar' ? (opt.label_ar || opt.label_en || opt) : (opt.label_en || opt.label_ar || opt)}
                                                                />
                                                            ))}
                                                        </RadioGroup>
                                                    </FormControl>
                                                </Grid>
                                            )

                                        case 'checkbox':
                                            return (
                                                <Grid item xs={12} md={fieldWidth} key={field.id}>
                                                    <FormControl component="fieldset">
                                                        <FormLabel component="legend">{fieldLabel}{field.required && ' *'}</FormLabel>
                                                        {options.map((opt, idx) => {
                                                            const optValue = opt.value || opt.label_en || opt
                                                            const currentValues = Array.isArray(fieldValue) ? fieldValue : []
                                                            return (
                                                                <FormControlLabel
                                                                    key={idx}
                                                                    control={
                                                                        <Checkbox
                                                                            checked={currentValues.includes(optValue)}
                                                                            onChange={(e) => {
                                                                                if (e.target.checked) {
                                                                                    handleChange([...currentValues, optValue])
                                                                                } else {
                                                                                    handleChange(currentValues.filter(v => v !== optValue))
                                                                                }
                                                                            }}
                                                                        />
                                                                    }
                                                                    label={language === 'ar' ? (opt.label_ar || opt.label_en || opt) : (opt.label_en || opt.label_ar || opt)}
                                                                />
                                                            )
                                                        })}
                                                    </FormControl>
                                                </Grid>
                                            )

                                        case 'date':
                                            return (
                                                <Grid item xs={12} md={fieldWidth} key={field.id}>
                                                    <TextField
                                                        fullWidth
                                                        type="date"
                                                        label={fieldLabel}
                                                        required={field.required}
                                                        value={fieldValue}
                                                        onChange={(e) => handleChange(e.target.value)}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                </Grid>
                                            )

                                        case 'time':
                                            return (
                                                <Grid item xs={12} md={fieldWidth} key={field.id}>
                                                    <TextField
                                                        fullWidth
                                                        type="time"
                                                        label={fieldLabel}
                                                        required={field.required}
                                                        value={fieldValue}
                                                        onChange={(e) => handleChange(e.target.value)}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                </Grid>
                                            )

                                        case 'textarea':
                                            return (
                                                <Grid item xs={12} md={fieldWidth} key={field.id}>
                                                    <TextField
                                                        fullWidth
                                                        multiline
                                                        rows={4}
                                                        label={fieldLabel}
                                                        required={field.required}
                                                        value={fieldValue}
                                                        onChange={(e) => handleChange(e.target.value)}
                                                    />
                                                </Grid>
                                            )

                                        case 'email':
                                            return (
                                                <Grid item xs={12} md={fieldWidth} key={field.id}>
                                                    <TextField
                                                        fullWidth
                                                        type="email"
                                                        label={fieldLabel}
                                                        required={field.required}
                                                        value={fieldValue}
                                                        onChange={(e) => handleChange(e.target.value)}
                                                    />
                                                </Grid>
                                            )

                                        case 'number':
                                        case 'phone':
                                            return (
                                                <Grid item xs={12} md={fieldWidth} key={field.id}>
                                                    <TextField
                                                        fullWidth
                                                        type={field.type === 'phone' ? 'tel' : 'number'}
                                                        label={fieldLabel}
                                                        required={field.required}
                                                        value={fieldValue}
                                                        onChange={(e) => handleChange(e.target.value)}
                                                    />
                                                </Grid>
                                            )

                                        case 'file':
                                            return (
                                                <Grid item xs={12} md={fieldWidth} key={field.id}>
                                                    <FormControl fullWidth>
                                                        <FormLabel sx={{ mb: 1 }}>
                                                            {fieldLabel}{field.required && ' *'}
                                                        </FormLabel>
                                                        <Button
                                                            variant="outlined"
                                                            component="label"
                                                            fullWidth
                                                            sx={{ py: 1.5, justifyContent: 'flex-start' }}
                                                        >
                                                            {fieldValue ?
                                                                (Array.isArray(fieldValue) ? `${fieldValue.length} file(s) selected` : fieldValue.name || t('common.fileSelected', 'File selected'))
                                                                : t('common.chooseFile', 'Choose File(s)')}
                                                            <input
                                                                type="file"
                                                                hidden
                                                                multiple={field.multiple !== false}
                                                                accept={field.accept || '*/*'}
                                                                onChange={(e) => {
                                                                    const files = Array.from(e.target.files)
                                                                    if (files.length > 0) {
                                                                        handleChange(field.multiple !== false ? files : files[0])
                                                                    }
                                                                }}
                                                            />
                                                        </Button>
                                                        {fieldValue && Array.isArray(fieldValue) && (
                                                            <Box sx={{ mt: 1 }}>
                                                                {fieldValue.map((file, idx) => (
                                                                    <Chip
                                                                        key={idx}
                                                                        label={file.name}
                                                                        size="small"
                                                                        onDelete={() => {
                                                                            handleChange(fieldValue.filter((_, i) => i !== idx))
                                                                        }}
                                                                        sx={{ mr: 0.5, mb: 0.5 }}
                                                                    />
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </FormControl>
                                                </Grid>
                                            )

                                        // Default: text, name, etc.
                                        default:
                                            return (
                                                <Grid item xs={12} md={fieldWidth} key={field.id}>
                                                    <TextField
                                                        fullWidth
                                                        label={fieldLabel}
                                                        required={field.required}
                                                        value={fieldValue}
                                                        onChange={(e) => handleChange(e.target.value)}
                                                    />
                                                </Grid>
                                            )
                                    }
                                })}
                        </Grid>
                    ) : (
                        <Typography color="text.secondary">
                            {t('common.loading')}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateSubmission}
                        disabled={creating}
                        startIcon={creating ? <CircularProgress size={16} /> : <AddIcon />}
                    >
                        {t('common.create')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Export Dialog with Column Management */}
            <Dialog
                open={exportDialogOpen}
                onClose={() => setExportDialogOpen(false)}
                maxWidth={false}
                PaperProps={{ sx: { maxWidth: 630, width: '100%' } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DownloadIcon color="primary" />
                    {language === 'ar' ? 'اختر لغة التصدير' : 'Choose Export Language'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {language === 'ar'
                            ? 'اختر اللغة التي تريد استخدامها لأسماء الأعمدة في ملف Excel'
                            : 'Choose the language for column headers in the Excel file'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                cursor: 'pointer',
                                border: '2px solid',
                                borderColor: exportLanguage === 'en' ? 'primary.main' : 'divider',
                                bgcolor: exportLanguage === 'en' ? 'action.selected' : 'background.paper',
                                '&:hover': { bgcolor: 'action.hover' }
                            }}
                            onClick={() => setExportLanguage('en')}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                    sx={{
                                        width: 20, height: 20, borderRadius: '50%',
                                        border: '2px solid',
                                        borderColor: exportLanguage === 'en' ? 'primary.main' : 'divider',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}
                                >
                                    {exportLanguage === 'en' && (
                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main' }} />
                                    )}
                                </Box>
                                <Box>
                                    <Typography fontWeight={600}>English</Typography>
                                    <Typography variant="caption" color="text.secondary">Column headers in English</Typography>
                                </Box>
                            </Box>
                        </Paper>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                cursor: 'pointer',
                                border: '2px solid',
                                borderColor: exportLanguage === 'ar' ? 'primary.main' : 'divider',
                                bgcolor: exportLanguage === 'ar' ? 'action.selected' : 'background.paper',
                                '&:hover': { bgcolor: 'action.hover' }
                            }}
                            onClick={() => setExportLanguage('ar')}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                    sx={{
                                        width: 20, height: 20, borderRadius: '50%',
                                        border: '2px solid',
                                        borderColor: exportLanguage === 'ar' ? 'primary.main' : 'divider',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}
                                >
                                    {exportLanguage === 'ar' && (
                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main' }} />
                                    )}
                                </Box>
                                <Box>
                                    <Typography fontWeight={600}>العربية</Typography>
                                    <Typography variant="caption" color="text.secondary">أسماء الأعمدة بالعربية</Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Box>

                    {/* System Columns Selection */}
                    <Box sx={{ mt: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SettingsIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                    {language === 'ar' ? 'أعمدة النظام' : 'System Columns'}
                                </Typography>
                                <Chip
                                    label={(() => {
                                        const total = SYSTEM_COLUMNS.length;
                                        const selected = selectedSystemColumns === null ? total : selectedSystemColumns.length;
                                        return `${selected}/${total}`;
                                    })()}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: 10, fontWeight: 600, color: 'text.secondary', borderColor: 'grey.300' }}
                                />
                            </Box>
                            <Button
                                size="small"
                                onClick={() => {
                                    if (selectedSystemColumns === null || selectedSystemColumns.length === SYSTEM_COLUMNS.length) {
                                        setSelectedSystemColumns([])
                                    } else {
                                        setSelectedSystemColumns(null)
                                    }
                                }}
                                sx={{ textTransform: 'none', fontSize: 11, fontWeight: 500, color: 'text.secondary' }}
                            >
                                {(!selectedSystemColumns || selectedSystemColumns.length === SYSTEM_COLUMNS.length)
                                    ? (language === 'ar' ? 'إلغاء تحديد الكل' : 'Deselect All')
                                    : (language === 'ar' ? 'تحديد الكل' : 'Select All')}
                            </Button>
                        </Box>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1,
                                borderRadius: 2,
                                bgcolor: 'grey.50',
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                gap: 0.5,
                            }}
                        >
                            {SYSTEM_COLUMNS.map((col) => {
                                const label = exportLanguage === 'ar' ? col.labelAr : col.labelEn
                                const isChecked = selectedSystemColumns === null || selectedSystemColumns.includes(col.id)
                                return (
                                    <Paper
                                        key={col.id}
                                        elevation={0}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            px: 1, py: 0.25,
                                            borderRadius: 1,
                                            cursor: 'pointer',
                                            border: '1px solid',
                                            borderColor: isChecked ? 'grey.400' : 'grey.200',
                                            bgcolor: isChecked ? 'grey.100' : 'background.paper',
                                            transition: 'all 0.15s ease',
                                            '&:hover': { borderColor: 'grey.500', bgcolor: 'grey.100' },
                                        }}
                                        onClick={() => {
                                            const allIds = SYSTEM_COLUMNS.map(c => c.id)
                                            let current = selectedSystemColumns === null ? [...allIds] : [...selectedSystemColumns]
                                            if (isChecked) {
                                                current = current.filter(id => id !== col.id)
                                            } else {
                                                if (!current.includes(col.id)) current.push(col.id)
                                            }
                                            setSelectedSystemColumns(current.length === allIds.length ? null : current)
                                        }}
                                    >
                                        <Checkbox
                                            size="small"
                                            checked={isChecked}
                                            sx={{ p: 0.25, color: 'grey.400', '&.Mui-checked': { color: 'grey.600' } }}
                                            tabIndex={-1}
                                            disableRipple
                                        />
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: 12,
                                                fontWeight: isChecked ? 500 : 400,
                                                color: isChecked ? 'text.primary' : 'text.secondary',
                                                ml: 0.25,
                                                whiteSpace: 'nowrap',
                                                direction: exportLanguage === 'ar' ? 'rtl' : 'ltr',
                                            }}
                                        >
                                            {label}
                                        </Typography>
                                    </Paper>
                                )
                            })}
                        </Paper>
                    </Box>

                    {/* Form Field Selection */}
                    {exportFormFields.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LabelIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        {language === 'ar' ? 'حقول النموذج' : 'Form Fields'}
                                    </Typography>
                                    <Chip
                                        label={(() => {
                                            const total = exportFormFields.length;
                                            const selected = selectedExportFields === null ? total : selectedExportFields.length;
                                            return `${selected}/${total}`;
                                        })()}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
                                    />
                                </Box>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        if (selectedExportFields === null || selectedExportFields.length === exportFormFields.length) {
                                            setSelectedExportFields([])
                                        } else {
                                            setSelectedExportFields(null)
                                        }
                                    }}
                                    sx={{ textTransform: 'none', fontSize: 12, fontWeight: 500 }}
                                >
                                    {(!selectedExportFields || selectedExportFields.length === exportFormFields.length)
                                        ? (language === 'ar' ? 'إلغاء تحديد الكل' : 'Deselect All')
                                        : (language === 'ar' ? 'تحديد الكل' : 'Select All')}
                                </Button>
                            </Box>
                            <Paper
                                variant="outlined"
                                sx={{
                                    maxHeight: 240,
                                    overflow: 'auto',
                                    p: 1.5,
                                    borderRadius: 2,
                                    bgcolor: 'grey.50',
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                    gap: 0.5,
                                    '&::-webkit-scrollbar': { width: 6 },
                                    '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 3 },
                                }}
                            >
                                {exportFormFields.map((field) => {
                                    const fieldId = field.id || field.name
                                    const label = exportLanguage === 'ar'
                                        ? (field.label_ar || field.label_en || field.label || fieldId)
                                        : (field.label_en || field.label || fieldId)
                                    const isChecked = selectedExportFields === null || selectedExportFields.includes(fieldId)
                                    return (
                                        <Paper
                                            key={fieldId}
                                            elevation={0}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                px: 1.5, py: 0.5,
                                                borderRadius: 1.5,
                                                cursor: 'pointer',
                                                border: '1px solid',
                                                borderColor: isChecked ? 'primary.light' : 'grey.200',
                                                bgcolor: isChecked ? 'primary.50' : 'background.paper',
                                                transition: 'all 0.15s ease',
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    bgcolor: isChecked ? 'primary.50' : 'action.hover',
                                                },
                                            }}
                                            onClick={() => {
                                                const allIds = exportFormFields.map(f => f.id || f.name)
                                                let current = selectedExportFields === null ? [...allIds] : [...selectedExportFields]
                                                if (isChecked) {
                                                    current = current.filter(id => id !== fieldId)
                                                } else {
                                                    if (!current.includes(fieldId)) current.push(fieldId)
                                                }
                                                setSelectedExportFields(current.length === allIds.length ? null : current)
                                            }}
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={isChecked}
                                                sx={{
                                                    p: 0.5,
                                                    color: 'grey.400',
                                                    '&.Mui-checked': { color: 'primary.main' },
                                                }}
                                                tabIndex={-1}
                                                disableRipple
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: 13,
                                                    fontWeight: isChecked ? 500 : 400,
                                                    color: isChecked ? 'text.primary' : 'text.secondary',
                                                    ml: 0.5,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    direction: exportLanguage === 'ar' ? 'rtl' : 'ltr',
                                                }}
                                            >
                                                {label}
                                            </Typography>
                                        </Paper>
                                    )
                                })}
                            </Paper>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExportDialogOpen(false)}>
                        {language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmExport}
                        disabled={exporting}
                        startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                    >
                        {exporting
                            ? (language === 'ar' ? 'جاري التصدير...' : 'Exporting...')
                            : (language === 'ar' ? 'تصدير' : 'Export')}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Column Settings Dialog */}
            <Dialog
                open={columnSettingsOpen}
                onClose={() => setColumnSettingsOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SettingsIcon color="primary" />
                        {isRTL ? 'إعدادات الأعمدة' : 'Column Settings'}
                    </Box>
                    <IconButton size="small" onClick={() => setColumnSettingsOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {isRTL
                            ? 'حدد الأعمدة المراد عرضها. اسحب لإعادة الترتيب.'
                            : 'Select which columns to display. Drag to reorder.'}
                    </Typography>

                    {/* System Columns */}
                    <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                        {isRTL ? 'حقول النظام' : 'System Fields'}
                    </Typography>
                    <List disablePadding>
                        {columns.filter(col => col.type !== 'form_field').map((column) => {
                            const index = columns.findIndex(c => c.id === column.id);
                            return (
                                <ListItem
                                    key={column.id}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragOver={(e) => handleDragOver(e, index)}
                                    onDragEnd={handleDragEnd}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: draggedItem === index ? 'primary.main' : 'divider',
                                        borderRadius: 1,
                                        mb: 1,
                                        bgcolor: draggedItem === index ? 'action.selected' : 'background.paper',
                                        cursor: 'grab',
                                        '&:active': { cursor: 'grabbing' },
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <DragIndicator color="action" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={isRTL ? column.labelAr : column.labelEn}
                                        secondary={column.required ? (isRTL ? 'مطلوب' : 'Required') : null}
                                    />
                                    <Switch
                                        edge="end"
                                        checked={column.visible}
                                        onChange={() => handleColumnToggle(column.id)}
                                        disabled={column.required}
                                    />
                                </ListItem>
                            )
                        })}
                    </List>

                    {/* Form Field Columns */}
                    {columns.some(col => col.type === 'form_field') && (
                        <>
                            <Typography variant="subtitle2" sx={{ mb: 1, mt: 3 }}>
                                {isRTL ? 'حقول النماذج' : 'Form Fields'}
                            </Typography>
                            <List disablePadding>
                                {columns.filter(col => col.type === 'form_field').map((column) => {
                                    const index = columns.findIndex(c => c.id === column.id);
                                    return (
                                        <ListItem
                                            key={column.id}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, index)}
                                            onDragOver={(e) => handleDragOver(e, index)}
                                            onDragEnd={handleDragEnd}
                                            sx={{
                                                border: '1px solid',
                                                borderColor: draggedItem === index ? 'primary.main' : 'divider',
                                                borderRadius: 1,
                                                mb: 1,
                                                bgcolor: draggedItem === index ? 'action.selected' : 'background.paper',
                                                cursor: 'grab',
                                                '&:active': { cursor: 'grabbing' },
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <DragIndicator color="action" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={isRTL ? column.labelAr : column.labelEn}
                                                secondary={
                                                    <Typography component="span" variant="caption" color="text.secondary">
                                                        {column.fieldType}
                                                    </Typography>
                                                }
                                            />
                                            <Switch
                                                edge="end"
                                                checked={column.visible}
                                                onChange={() => handleColumnToggle(column.id)}
                                                disabled={column.required}
                                            />
                                        </ListItem>
                                    )
                                })}
                            </List>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        size="small"
                        onClick={() => {
                            setColumns(DS_DEFAULT_COLUMNS)
                            try { localStorage.removeItem(DS_STORAGE_KEY) } catch {}
                        }}
                    >
                        {isRTL ? 'استعادة الافتراضي' : 'Reset to Default'}
                    </Button>
                    <Button variant="contained" onClick={() => setColumnSettingsOpen(false)}>
                        {isRTL ? 'تم' : 'Done'}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>

    )
}

export default DirectSubmissions
